import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search,
  X,
  Utensils,
  Wine,
  Carrot,
  Truck,
  FileText,
  User,
  ClipboardCheck,
  Receipt,
  Layout,
  Loader2,
  Command
} from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon-Mapping
const iconMap = {
  "utensils": Utensils,
  "wine": Wine,
  "carrot": Carrot,
  "truck": Truck,
  "file-text": FileText,
  "user": User,
  "clipboard-check": ClipboardCheck,
  "receipt": Receipt,
  "layout": Layout,
};

// Typ-Farben
const typColors = {
  "speise": "bg-orange-500/20 text-orange-400",
  "getraenk": "bg-purple-500/20 text-purple-400",
  "zutat": "bg-green-500/20 text-green-400",
  "lieferant": "bg-blue-500/20 text-blue-400",
  "dokument": "bg-amber-500/20 text-amber-400",
  "mitarbeiter": "bg-pink-500/20 text-pink-400",
  "aufgabe": "bg-red-500/20 text-red-400",
  "ausgabe": "bg-rose-500/20 text-rose-400",
  "seite": "bg-violet-500/20 text-violet-400",
};

const typLabels = {
  "speise": "Speise",
  "getraenk": "Getränk",
  "zutat": "Zutat",
  "lieferant": "Lieferant",
  "dokument": "Dokument",
  "mitarbeiter": "Mitarbeiter",
  "aufgabe": "Aufgabe",
  "ausgabe": "Ausgabe",
  "seite": "Seite",
};

export const GlobalSearch = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [pageResults, setPageResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      setPageResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        // Parallel requests
        const [dataRes, pagesRes] = await Promise.all([
          axios.get(`${API}/suche?q=${encodeURIComponent(query)}&limit=15`, { withCredentials: true }),
          axios.get(`${API}/suche/seiten?q=${encodeURIComponent(query)}`, { withCredentials: true })
        ]);
        
        setResults(dataRes.data.ergebnisse || []);
        setPageResults(pagesRes.data.seiten || []);
        setSelectedIndex(0);
      } catch (error) {
        console.error("Suche fehlgeschlagen:", error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (!isOpen) {
      setQuery("");
      setResults([]);
      setPageResults([]);
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    const allResults = [...pageResults, ...results];
    
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && allResults[selectedIndex]) {
      e.preventDefault();
      handleSelect(allResults[selectedIndex]);
    } else if (e.key === "Escape") {
      onClose();
    }
  }, [results, pageResults, selectedIndex, onClose]);

  const handleSelect = (item) => {
    navigate(item.link);
    onClose();
  };

  const allResults = [...pageResults, ...results];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 bg-slate-900 border-slate-700 overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center gap-3 p-4 border-b border-slate-700">
          <Search className="text-slate-400" size={20} />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Suche nach Speisen, Getränken, Lieferanten, Seiten..."
            className="flex-1 bg-transparent border-none text-white text-lg placeholder:text-slate-500 focus-visible:ring-0"
            data-testid="global-search-input"
          />
          {loading && <Loader2 className="animate-spin text-slate-400" size={20} />}
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Command size={12} />
            <span>K</span>
          </div>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {query.length < 2 ? (
            <div className="p-8 text-center text-slate-500">
              <Search size={40} className="mx-auto mb-3 opacity-30" />
              <p>Mindestens 2 Zeichen eingeben</p>
              <p className="text-xs mt-2">Durchsucht: Speisen, Getränke, Zutaten, Lieferanten, Dokumente, Seiten</p>
            </div>
          ) : allResults.length === 0 && !loading ? (
            <div className="p-8 text-center text-slate-500">
              <p>Keine Ergebnisse für "{query}"</p>
            </div>
          ) : (
            <div className="py-2">
              {/* Seiten-Ergebnisse */}
              {pageResults.length > 0 && (
                <div className="px-3 py-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Seiten & Funktionen</p>
                  {pageResults.map((item, idx) => {
                    const Icon = iconMap[item.icon] || Layout;
                    const isSelected = idx === selectedIndex;
                    
                    return (
                      <button
                        key={`page-${idx}`}
                        onClick={() => handleSelect(item)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                          isSelected ? "bg-violet-600/20 border border-violet-500/50" : "hover:bg-slate-800"
                        }`}
                        data-testid={`search-result-page-${idx}`}
                      >
                        <div className={`p-2 rounded-lg ${typColors[item.typ] || "bg-slate-700"}`}>
                          <Icon size={16} />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-white font-medium">{item.titel}</p>
                          <p className="text-sm text-slate-400">{item.beschreibung}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${typColors[item.typ] || "bg-slate-700 text-slate-400"}`}>
                          Seite
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Daten-Ergebnisse */}
              {results.length > 0 && (
                <div className="px-3 py-2">
                  {pageResults.length > 0 && (
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 mt-2">Daten</p>
                  )}
                  {results.map((item, idx) => {
                    const Icon = iconMap[item.icon] || FileText;
                    const actualIndex = pageResults.length + idx;
                    const isSelected = actualIndex === selectedIndex;
                    
                    return (
                      <button
                        key={`data-${idx}`}
                        onClick={() => handleSelect(item)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                          isSelected ? "bg-violet-600/20 border border-violet-500/50" : "hover:bg-slate-800"
                        }`}
                        data-testid={`search-result-data-${idx}`}
                      >
                        <div className={`p-2 rounded-lg ${typColors[item.typ] || "bg-slate-700"}`}>
                          <Icon size={16} />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-white font-medium">{item.titel}</p>
                          <p className="text-sm text-slate-400">{item.untertitel}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${typColors[item.typ] || "bg-slate-700 text-slate-400"}`}>
                          {typLabels[item.typ] || item.typ}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-700 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-800 rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-slate-800 rounded">↓</kbd>
              Navigation
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-800 rounded">↵</kbd>
              Auswählen
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-800 rounded">Esc</kbd>
              Schließen
            </span>
          </div>
          <span>{allResults.length} Ergebnisse</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Search Button für Sidebar/Header
export const SearchButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 w-full px-3 py-2 text-left text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-all group"
      data-testid="global-search-btn"
    >
      <Search size={18} />
      <span className="flex-1">Suche...</span>
      <div className="flex items-center gap-0.5 text-xs opacity-50 group-hover:opacity-100">
        <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-[10px]">⌘</kbd>
        <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-[10px]">K</kbd>
      </div>
    </button>
  );
};

export default GlobalSearch;
